/**
 * ACTION BOARD — the first thing on the rail, and the only panel that tells you
 * what to DO rather than what the numbers are.
 *
 * Every other panel answers "what is the reading?". This one answers "what needs
 * a decision right now?" — ranked, with the triggering evidence and a next step.
 * Rules and thresholds live in `lib/actionBoard.ts` (pure + unit-tested).
 */

import { useMemo } from "react";
import type { ActionItem, ActionSeverity, ActionBoardInput } from "../lib/actionBoard";
import { evaluateActionBoard, boardSeverity } from "../lib/actionBoard";
import { PanelHeader } from "./PanelHeader";
import { useLang } from "../hooks/useLang";

interface Props extends ActionBoardInput {
  loading?: boolean;
}

const SEVERITY_COLOR: Record<ActionSeverity, string> = {
  critical: "var(--bad)",
  warning: "var(--warn)",
  watch: "var(--data)",
  blindspot: "var(--ink-low)",
};

const SEVERITY_LABEL: Record<ActionSeverity, { en: string; th: string }> = {
  critical: { en: "ACT NOW", th: "ดำเนินการทันที" },
  warning: { en: "PREPARE", th: "เตรียมพร้อม" },
  watch: { en: "WATCH", th: "เฝ้าระวัง" },
  blindspot: { en: "BLIND SPOT", th: "จุดบอดข้อมูล" },
};

export function ActionBoard({ loading, ...input }: Props) {
  const { lang } = useLang();
  const th = lang === "th";
  const items = useMemo(() => evaluateActionBoard(input), [input]);

  const actionable = items.filter((i) => i.severity !== "blindspot");
  const blindspots = items.filter((i) => i.severity === "blindspot");

  // At-a-glance headline severity, so the rail is scannable without reading rows.
  const headline = boardSeverity(items);
  const topCount = headline && headline !== "blindspot"
    ? actionable.filter((i) => i.severity === headline).length
    : blindspots.length;

  return (
    <div className="col action-board">
      <PanelHeader
        title={th ? "ต้องทำอะไรตอนนี้" : "WHAT NEEDS ATTENTION"}
        source="rules over live feeds"
        actions={
          headline ? (
            <span className="mono" style={{ color: SEVERITY_COLOR[headline], fontSize: "0.7rem" }}>
              {topCount} {th ? SEVERITY_LABEL[headline].th : SEVERITY_LABEL[headline].en}
            </span>
          ) : undefined
        }
      />

      {/* "evaluating" only while genuinely nothing is known — every monitored
          feed is still on its first load. The moment ANY feed resolves, the
          board must show a definite state: a spinner that never ends is the
          exact failure this panel exists to prevent. */}
      {loading ? (
        <div className="mono eyebrow" style={{ color: "var(--text-3)", marginTop: 8 }}>
          {th ? "กำลังประเมินสถานการณ์…" : "evaluating…"}
        </div>
      ) : actionable.length === 0 ? (
        /* "All clear" is only honest when we can actually see. If signals are
           down, say so here rather than showing a reassuring green line. */
        blindspots.length > 0 ? (
          <div style={{ marginTop: 8 }}>
            <div className="mono" style={{ color: "var(--warn)", fontSize: "0.95rem" }}>
              {th ? "ไม่ทราบสถานะ — ข้อมูลไม่ครบ" : "STATUS UNKNOWN — not all signals reporting"}
            </div>
            <div className="eyebrow mono" style={{ color: "var(--text-3)", marginTop: 2 }}>
              {th
                ? `ไม่มีรายการเกินเกณฑ์ แต่มี ${blindspots.length} สัญญาณที่ไม่ส่งข้อมูล`
                : `no thresholds crossed, but ${blindspots.length} signal${blindspots.length > 1 ? "s are" : " is"} not reporting`}
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 8 }}>
            <div className="mono" style={{ color: "var(--good)", fontSize: "0.95rem" }}>
              {th ? "ปกติ — ไม่มีรายการเกินเกณฑ์" : "ALL CLEAR — nothing over threshold"}
            </div>
            <div className="eyebrow mono" style={{ color: "var(--text-3)", marginTop: 2 }}>
              {th
                ? "ทุกสัญญาณที่เฝ้าระวังอยู่ต่ำกว่าเกณฑ์เตือนภัย"
                : "every monitored signal is below its alert threshold"}
            </div>
          </div>
        )
      ) : (
        <ol className="action-list" style={{ listStyle: "none", padding: 0, margin: "8px 0 0" }}>
          {actionable.map((item, index) => (
            <ActionRow key={item.id} item={item} th={th} expanded={index === 0 || item.severity === "critical"} />
          ))}
        </ol>
      )}

      {/* Blind spots sit below the fold of the real items — present, never loud. */}
      {blindspots.length > 0 && (
        <details className="detail-disclosure">
          <summary className="mono">
            {th
              ? `จุดบอดข้อมูล ${blindspots.length} รายการ`
              : `${blindspots.length} blind spot${blindspots.length > 1 ? "s" : ""}`}
          </summary>
          <div className="detail-disclosure-body">
            {blindspots.map((item) => (
              <ActionRow key={item.id} item={item} th={th} />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function ActionRow({ item, th, expanded = true }: { item: ActionItem; th: boolean; expanded?: boolean }) {
  const color = SEVERITY_COLOR[item.severity];
  const heading = <>
    <span className="eyebrow mono action-severity" style={{ color }}>{th ? SEVERITY_LABEL[item.severity].th : SEVERITY_LABEL[item.severity].en}</span>
    <span className="mono action-title">{th ? item.title.th : item.title.en}</span>
  </>;
  const explanation = <>
    <div className="action-evidence" style={{ color: "var(--text-3)", marginTop: 2 }}>{item.evidence}</div>
    <div style={{ fontSize: "0.8rem", marginTop: 3, color: "var(--ink-mid)" }}>→ {th ? item.action.th : item.action.en}</div>
  </>;
  return (
    <li className="action-item" style={{ borderLeft: `3px solid ${color}`, paddingLeft: 8, marginBottom: 10 }}>
      {expanded ? <>{heading}{explanation}</> : (
        <details className="action-detail">
          <summary>{heading}</summary>
          {explanation}
        </details>
      )}
    </li>
  );
}
